import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  MessageSquare, Inbox, Clock, CheckCircle2, XCircle, Send,
  Phone, Mail, Calendar, Users, MapPin, Loader2,
} from "lucide-react";

const STATUS_CONFIG = {
  new: { label: "New", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300", icon: Inbox },
  in_progress: { label: "In Progress", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300", icon: Clock },
  quoted: { label: "Quoted", color: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300", icon: Send },
  converted: { label: "Converted", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300", icon: CheckCircle2 },
  closed: { label: "Closed", color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300", icon: XCircle },
};

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-lg font-bold">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

export default function AdminEnquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const [enqRes, statsRes] = await Promise.all([
        api.get("/admin/enquiries", { params }),
        api.get("/admin/enquiries/stats"),
      ]);
      setEnquiries(enqRes.data.items || enqRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEnquiries(); }, [statusFilter]);

  const updateStatus = async (id, status) => {
    setSaving(true);
    try {
      const res = await api.put(`/admin/enquiries/${id}`, { status });
      setSelected(res.data);
      fetchEnquiries();
      toast.success(`Status updated to ${STATUS_CONFIG[status]?.label || status}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const addNote = async (id) => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      const res = await api.put(`/admin/enquiries/${id}`, { new_note: newNote });
      setSelected(res.data);
      setNewNote("");
      toast.success("Note added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-enquiries-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-primary" />
            Enquiries
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Manage customer enquiries and leads</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="enquiry-stats">
        <StatCard label="Total" value={stats.total || 0} icon={MessageSquare} color="bg-muted" />
        <StatCard label="New" value={stats.new || 0} icon={Inbox} color="bg-blue-100 dark:bg-blue-900/30 text-blue-600" />
        <StatCard label="In Progress" value={stats.in_progress || 0} icon={Clock} color="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600" />
        <StatCard label="Quoted" value={stats.quoted || 0} icon={Send} color="bg-purple-100 dark:bg-purple-900/30 text-purple-600" />
        <StatCard label="Converted" value={stats.converted || 0} icon={CheckCircle2} color="bg-green-100 dark:bg-green-900/30 text-green-600" />
        <StatCard label="Closed" value={stats.closed || 0} icon={XCircle} color="bg-gray-100 dark:bg-gray-900/30 text-gray-600" />
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="enquiry-status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="quoted">Quoted</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />)}
        </div>
      ) : enquiries.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">
          <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No enquiries found</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="enquiry-list">
          {enquiries.map((enq) => {
            const cfg = STATUS_CONFIG[enq.status] || STATUS_CONFIG.new;
            return (
              <Card
                key={enq.id}
                className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelected(enq)}
                data-testid={`enquiry-row-${enq.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium truncate">{enq.full_name || enq.name || "Unknown"}</span>
                      <Badge className={`${cfg.color} text-xs`}>{cfg.label}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground truncate">
                      {enq.product_title || "General Enquiry"} 
                      {enq.travel_date && ` • ${enq.travel_date}`}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{enq.phone}</span>
                      {enq.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{enq.email}</span>}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap">
                    {fmt(enq.created_at)}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail Sheet */}
      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent className="w-full sm:w-[480px] overflow-y-auto" data-testid="enquiry-detail-sheet">
          <SheetHeader>
            <SheetTitle>Enquiry Details</SheetTitle>
          </SheetHeader>
          {selected && (
            <div className="mt-4 space-y-5">
              {/* Customer info */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Customer</h3>
                <div className="text-sm space-y-1">
                  <p className="flex items-center gap-2"><Users className="w-4 h-4 text-muted-foreground" />{selected.full_name || selected.name}</p>
                  <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground" />{selected.phone}</p>
                  {selected.email && <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-muted-foreground" />{selected.email}</p>}
                  {selected.pickup_city && <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" />{selected.pickup_city}</p>}
                </div>
              </div>

              {/* Trip info */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Trip Details</h3>
                <div className="text-sm space-y-1 bg-muted/50 p-3 rounded-lg">
                  {selected.product_title && <p><strong>Product:</strong> {selected.product_title}</p>}
                  {selected.travel_date && <p className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {selected.travel_date}</p>}
                  <p><strong>Pax:</strong> {selected.adults || 1} adults{selected.children > 0 ? `, ${selected.children} children` : ""}</p>
                  {selected.message && <p className="mt-2"><strong>Message:</strong> {selected.message}</p>}
                </div>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Status</h3>
                <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                  <SelectTrigger data-testid="enquiry-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="quoted">Quoted</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Notes</h3>
                {(selected.notes || []).length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selected.notes.map((n, i) => (
                      <div key={i} className="text-xs bg-muted/50 p-2 rounded">
                        <span className="font-medium">{n.by}</span> · {fmt(n.at)}
                        <p className="mt-0.5">{n.text}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No notes yet</p>
                )}
                <div className="flex gap-2">
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    rows={2}
                    className="text-sm"
                    data-testid="enquiry-note-input"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={() => addNote(selected.id)}
                  disabled={saving || !newNote.trim()}
                  data-testid="enquiry-note-submit"
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Add Note
                </Button>
              </div>

              <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                Created: {fmt(selected.created_at)} · Source: {selected.source}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
